<template>
  <Accordion
    :multiple="false"
    :activeIndex="activeIndex"
    class="single-accordion"
    @tab-open="onOpen"
    @tab-close="onClose"
  >
    <AccordionPanel value="main">
      <AccordionHeader>
        <span>
          {{ title }}
          <span v-if="innerOpen">{{ titleOpen }}</span>
          <span v-else></span>
          {{ titleClosed }}
        </span>
      </AccordionHeader>
      <AccordionContent>
        <KeepAlive v-if="keepAlive">
          <slot v-if="!lazy || isOpen" />
        </KeepAlive>
        <template v-else>
          <slot v-if="!lazy || isOpen" />
        </template>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Accordion from 'primevue/accordion';

const props = withDefaults(
  defineProps<{
    open?: boolean;
    defaultOpen?: boolean;
    title?: string;
    titleOpen?: string;
    titleClosed?: string;
    disabled?: boolean;
    lazy?: boolean;
    keepAlive?: boolean;
  }>(),
  {
    defaultOpen: false,
    title: '',
    titleOpen: '',
    titleClosed: '',
    disabled: false,
    lazy: false,
    keepAlive: false,
  },
);

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const isControlled = computed(() => props.open !== undefined);
const innerOpen = ref<boolean>(props.defaultOpen);

watch(
  () => props.defaultOpen,
  (v) => {
    if (!isControlled.value) innerOpen.value = !!v;
  },
);

const isOpen = computed<boolean>({
  get() {
    return isControlled.value ? !!props.open : innerOpen.value;
  },
  set(v) {
    if (props.disabled) return;
    if (isControlled.value) emit('update:open', v);
    else innerOpen.value = v;
  },
});

const activeIndex = computed(() => (isOpen.value ? 0 : null));

function onOpen() {
  if (props.disabled) return;
  isOpen.value = true;
  emit('open');
}

function onClose() {
  if (props.disabled) return;
  isOpen.value = false;
  emit('close');
}
</script>
